import { useNavigate } from 'react-router';
import { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Button, Typography } from '@navi/web-ui/lib/primitives';
import styles from './index.module.scss';
import BackIcon from '@cp/src/assets/images/icons/BackIcon';
import NaviTable from '@cp/src/components/NaviTable/NaviTable';
import { getConfigureReportColumnDefs } from './TableContent';
import ConfigFormDrawer from '../ConfigDrawer';
import { setIsDrawerOpen, setIsModalOpen } from '@cp/src/reducers/configureReportsSlice';
import { getConfigureReports, postColumnConfiguration } from '../action';
import Modal from '@cp/src/components/Modal/Modal';
import { calculateTableHeight, getReportTypeDisplayName, getTeamTypeDisplayName } from '../utils';
import { RootState } from '@cp/src/store';
import { OnSubmitFormData, SelectedReport, TableRow } from '../interface';

const ConfigureReports = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const [isEditable, setIsEditable] = useState(false);
  const [selectedReport, setSelectedReport] = useState<SelectedReport | null>(null);
  const [pendingFormData, setPendingFormData] = useState<OnSubmitFormData | null>(null);

  const isModalOpen = useSelector((state: RootState) => state.configureReport?.isModalOpen);
  const isDrawerOpen = useSelector((state: RootState) => state.configureReport?.isDrawerOpen);
  const configureReportsData = useSelector(
    (state: RootState) => state.configureReport?.configureReportsData
  );
  const configureReportsLoading = useSelector(
    (state: RootState) => state.configureReport?.configureReportsLoading
  );

  useEffect(() => {
    dispatch(getConfigureReports());
  }, [dispatch]);

  const handleView = (row: TableRow) => {
    setSelectedReport(row);
    setIsEditable(false);
    dispatch(setIsDrawerOpen(true));
  };

  const handleDrawerClosed = () => {
    dispatch(setIsDrawerOpen(false));
  };

  const handleModalCancel = () => {
    dispatch(setIsModalOpen(false));
    setPendingFormData(null);
  };

  const handleModalConfirmSubmit = () => {
    if (pendingFormData && selectedReport) {
      const { reportType, teamType, selectedColumns } = pendingFormData;
      dispatch(postColumnConfiguration(reportType, teamType, selectedColumns));
      dispatch(setIsModalOpen(false));
      dispatch(setIsDrawerOpen(false));
      setPendingFormData(null);
    }
  };

  const handleShowConfirmationModal = (formData: OnSubmitFormData) => {
    setPendingFormData(formData);
    dispatch(setIsModalOpen(true));
  };

  const columns = getConfigureReportColumnDefs(handleView);

  return (
    <div className={styles.reportsContainer}>
      <div className={styles.reportsHeader}>
        <div className={'flex flex-row items-center gap-4 my-4 ml-8'}>
          <BackIcon
            className="cursor-pointer"
            size="24"
            color="var(--navi-color-gray-c2)"
            onClick={() => navigate(-1)}
          />

          <Typography variant="h2" color="var(--navi-color-gray-c2)">
            Configure Reports
          </Typography>
        </div>
      </div>
      <div
        className={styles.reportsGrid}
        style={{ height: `${calculateTableHeight(configureReportsData?.length || 0)}px` }}
      >
        <NaviTable
          tableName={'reports'}
          columns={columns}
          data={configureReportsData || []}
          totalElements={configureReportsData?.length || 0}
          loading={configureReportsLoading}
          showPaginationComponent={false}
          onRowClick={row => handleView(row)}
          enableRowClick={() => true}
        />
      </div>
      {isDrawerOpen ? (
        <ConfigFormDrawer
          isDrawerOpen={isDrawerOpen}
          onClose={handleDrawerClosed}
          isEditable={isEditable}
          setIsEditable={setIsEditable}
          selectedReport={selectedReport}
          onSubmitForm={handleShowConfirmationModal}
        />
      ) : null}
      {isModalOpen ? (
        <Modal
          visible={isModalOpen}
          onClose={handleModalCancel}
          closeOnOutsideClick={false}
          header=""
          showCloseIcon={false}
          contentWrapperClass="!w-[400px]"
        >
          <div className="flex flex-col items-center p-6">
            <Typography variant="h3" className="text-center mb-6">
              Are you sure you want to configure{' '}
              <strong>{getReportTypeDisplayName(selectedReport?.baseReportType || '')}</strong> for{' '}
              <strong>{getTeamTypeDisplayName(selectedReport?.teamType || '')}</strong>?
            </Typography>
            <div className="flex gap-4 w-full">
              <Button variant="secondary" onClick={handleModalCancel} className="flex-1">
                Cancel
              </Button>
              <Button variant="primary" onClick={handleModalConfirmSubmit} className="flex-1">
                Confirm
              </Button>
            </div>
          </div>
        </Modal>
      ) : null}
    </div>
  );
};

export default ConfigureReports;
